import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {View, Text, Platform, InputAccessoryView, Keyboard} from 'react-native';
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field';
import * as Keychain from 'react-native-keychain';
import Popup from '../../elements/Popup';
import Button from '../../elements/Button';
import DIAText from '../../elements/Text';
import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../../theme';
import {
  BUTTON_TYPES,
  KEYS,
  SEND_OTP_TYPES,
  USER_AUTH_STATUS,
  CONDITIONS,
} from '../../../utils/Constants';
import {
  CHANGE_PHONE,
  HTTP_METHODS,
  SEND_OTP,
  VERIFY_OTP,
  loginAndSaveDetailsGlobal,
} from '../../../utils/URLConstants';
import {useNavigation} from '@react-navigation/native';
import {fetchApi} from '../../../utils/ApiHelper';
import {transformPhoneForAPI} from '../../../utils/HelperMethods';
import {
  getData,
  getUserPhoneNumber,
  getUserProfileData,
  storeData,
  storeUserProfileData,
} from '../../../utils/GeneralStorage';
import {loadingPauseContext} from '../../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../../contexts/ErrorStatusContext';
import {eligibilityCheckContext} from '../../../contexts/EligibilityCheckContext';
import {tID} from '../../../utils/TestIdHelper';
import CommonStyles from '../../../utils/CommonStyles';
import {useNavigate} from '../../../utils/mockedWebLibs/react-router-dom';
import Config from 'react-native-config';

const CELL_COUNT = 6;

const CodeResend = ({
  codeResendVisibility = false,
  resendCode = () => {},
  changePopupVisibility = () => {},
  phoneNo = '',
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <Popup
      testID={tID('VerifyCode-Popup-Resend')}
      title={'Code Resent'}
      visible={codeResendVisibility}
      description={
        'Check your phone messages to verify if you have received it.'
      }>
      <Button
        testID={tID('VerifyCode-Button-ResendAgain')}
        style={styles.leftButton}
        onPress={() => resendCode(phoneNo)}
        title={'Resend Again'}
      />
      <Button
        testID={tID('VerifyCode-Button-Continue')}
        style={styles.rightButton}
        onPress={changePopupVisibility}
        title={'Continue'}
        buttonType={BUTTON_TYPES.Filled}
        titleColor={colors.textOnAccent}
      />
    </Popup>
  );
};

const ContactSupport = ({
  showContactSupport = false,
  setShowContactSupport = () => {},
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <Popup
      testID={tID('VerifyCode-Popup-Contact')}
      title={'Contact Support'}
      visible={showContactSupport}
      description={
        <>
          If you're having trouble verifying your phone, please reach out to{' '}
          <DIAText
            testID={tID('VerifyCode-DIAText-CustomerService')}
            variation="Body1SemiBold"
            color={colors.textMuted}>
            {Config.DIATHRIVE_SUPPORT_EMAIL}
          </DIAText>{' '}
          or try again after 1 hour.
        </>
      }>
      <Button
        testID={tID('VerifyCode-Button-Close')}
        style={[styles.leftButton]}
        onPress={() => setShowContactSupport(false)}
        title={'Close'}
        titleColor={colors.link}
      />
    </Popup>
  );
};

const VerifyCode = forwardRef(
  (
    {
      phoneNo,
      type = SEND_OTP_TYPES.existing,
      nextScreen = 'PasswordRecovery',
      needsLogin = false,
      showButtons = true,
      onCodeChange = () => {},
      onVerificationSuccess,
      authCredentials,
      setAuthCredentials, //needed to set the recovery password token
    },
    ref,
  ) => {
    const {colors} = useTheme();
    const styles = useThemedStyles(makeStyles);
    useImperativeHandle(ref, () => ({
      verify: () => verifyOTP(),
      resend: phoneNumber => resendCode(phoneNumber),
    }));

    // Both hooks run unconditionally (rules-of-hooks); each is a no-op stub on
    // the other platform (mockedWebLibs/react-router-dom on native, the web
    // bundler's '@react-navigation/native' alias on web).
    const webNavigate = useNavigate();
    const nativeNavigation = useNavigation();
    const navigation = Platform.OS === 'web' ? webNavigate : nativeNavigation;

    const hasLocked = useRef(false);
    const [value, setValue] = useState('');
    const [hasError, setHasError] = useState(false);
    const [codeResendVisibility, setCodeResendVisibility] = useState(false);
    const [showContactSupport, setShowContactSupport] = useState(false);
    const blurFullfillRef = useBlurOnFulfill({value, cellCount: CELL_COUNT});
    const [props, getCellOnLayoutHandler] = useClearByFocusCell({
      value,
      setValue,
    });

    const lpc = useContext(loadingPauseContext);
    const esc = useContext(errorStatusContext);
    const ecc = useContext(eligibilityCheckContext);

    const isWebPlatform = useMemo(() => {
      return Platform.OS === 'web';
    }, []);

    useEffect(() => {
      if (isWebPlatform) onCodeChange(value);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- onCodeChange is an unstable inline prop (and default); adding it would re-fire this effect on every parent render instead of only on value change
    }, [isWebPlatform, value]);

    useEffect(() => {
      // if (nextScreen === 'DiabetesType') {
      getData(KEYS.OTPnumAttemptKey) //logic for making the modal appear/disappear
        .then(numAttempts => {
          if (numAttempts >= CONDITIONS.numAttemptsAllowed) {
            getData(KEYS.OTPblockedTimeKey).then(blockedTime => {
              let timeDiff = new Date() - Date.parse(blockedTime);
              if (CONDITIONS.timeDiffAllowed < timeDiff) {
                hasLocked.current = false;
                storeData(KEYS.OTPnumAttemptKey, '0');
              } else {
                hasLocked.current = true;
                setShowContactSupport(true);
              }
            });
          }
        });
      // }
    }, []);

    const updateNumAttempts = () => {
      getData(KEYS.OTPnumAttemptKey).then(numAttempts => {
        numAttempts ? numAttempts++ : (numAttempts = 1);
        if (numAttempts == CONDITIONS.numAttemptsAllowed) {
          //set the blocker time once
          storeData(KEYS.OTPblockedTimeKey, new Date().toString());
          setShowContactSupport(true);
          hasLocked.current = true;
        }
        storeData(KEYS.OTPnumAttemptKey, '' + numAttempts);
      });
    };

    const resendCode = async phoneNumber => {
      let credentials;
      if (Platform.OS === 'web') {
        credentials = authCredentials;
      } else {
        //fetch the user details from store
        credentials = await Keychain.getGenericPassword();
      }
      if (hasLocked.current == true) {
        setShowContactSupport(true);
        return;
      }
      const postData = {
        phone: transformPhoneForAPI(phoneNumber),
        email: credentials?.username,
        type,
      };
      // DLog(postData);
      lpc.setVisible(true);
      fetchApi(HTTP_METHODS.POST, SEND_OTP, postData)
        .then(response => {
          lpc.setVisible(false);
          if (response.status == 200) {
            setHasError(false);
            setTimeout(() => {
              setCodeResendVisibility(true);
            }, 1000);
          }
        })
        .catch(error => {
          // DLog(error);
          lpc.setVisible(false);
          esc.showError(error);
          // if (error.data) {
          //   if (error.data.errors instanceof Array) {
          //     alert(error.data.errors[0].message);
          //   } else if (typeof error.data.errors === 'string') {
          //     alert(error.data.errors);
          //   }
          // }
        });
    };

    const updateProfileData = async phone => {
      const profile = await getUserProfileData();
      let userProfile = {...profile};
      userProfile.phone = phone;
      // DLog('PROFILE', userProfile);
      storeUserProfileData(userProfile);
    };

    const loginAndSaveDetails = async () => {
      loginAndSaveDetailsGlobal(
        authCredentials?.username,
        authCredentials?.password,
      )
        .then(async res => {
          lpc.setVisible(false);
          if (res.status === 200) {
            //store the received user token
            // DLog(res.data.data);
            const profile = res.data.data;
            if (Platform.OS === 'web') {
              //API has updated the variable name for token
              await storeData(KEYS.userToken, profile.accessToken);
            } else await storeData(KEYS.userToken, profile.token);
            await storeData(KEYS.isLogin, USER_AUTH_STATUS.login);
            await storeData(KEYS.userId, profile.id);
            await storeUserProfileData(profile);

            //checking user employer needs eligibility check or not
            if (profile.employer.realtimeFlowId === 0) {
              //redirecting to normal flow if user employer doesn't need eligibility check
              await storeData(KEYS.userEligible, 'true');
              ecc.setIsEligible(true);
            } else {
              //checking Eligibilty based on employer realtimeFlowId
              ecc.setPreviousScreen('SignupPhoneVerification');
              ecc.checkEligibilty(
                navigation,
                profile.firstname + ' ' + profile.lastname,
                profile.email,
                profile.dateOfBirth,
                profile.subscriberId,
                profile.employer.realtimeFlowId,
              );
            }
            if (isWebPlatform && onVerificationSuccess) {
              onVerificationSuccess();
            } else {
              navigation.navigate(nextScreen);
            }
          }
        })
        .catch(e => {
          // DLog(e);
          lpc.setVisible(false);
          esc.showError(e);
        });
    };

    const changePhoneAPI = async () => {
      const oldPhone = await getUserPhoneNumber();
      const postData = {
        oldPhone: transformPhoneForAPI(oldPhone),
        newPhone: transformPhoneForAPI(phoneNo),
      };

      fetchApi(HTTP_METHODS.POST, CHANGE_PHONE, postData)
        .then(async response => {
          lpc.setVisible(false);
          if (response.status == 200) {
            await updateProfileData(transformPhoneForAPI(phoneNo));
            if (isWebPlatform && onVerificationSuccess) {
              onVerificationSuccess();
            } else {
              navigation.navigate(nextScreen);
            }
          }
        })
        .catch(error => {
          // DLog(error);
          lpc.setVisible(false);
          esc.showError(error);
        });
    };

    const verifyOTP = async () => {
      let credentials;
      if (Platform.OS === 'web') {
        credentials = authCredentials;
      } else {
        //fetch the user details from store
        credentials = await Keychain.getGenericPassword();
      }

      let email = credentials?.username;
      if (hasLocked.current == true) {
        setShowContactSupport(true);
        return;
      }
      const postData = {
        phone: transformPhoneForAPI(phoneNo),
        code: value,
        email,
        type,
      };
      lpc.setVisible(true);
      fetchApi(HTTP_METHODS.POST, VERIFY_OTP, postData)
        .then(response => {
          //lpc.setVisible(false);
          if (response.status == 200) {
            storeData(KEYS.OTPnumAttemptKey, '0');
            let params = {phone: phoneNo};
            if (
              response.data &&
              response.data.data &&
              response.data.data.passwordRecoveryToken
            ) {
              params = {
                ...params,
                token: response.data.data.passwordRecoveryToken,
              };
            }
            if (needsLogin) {
              loginAndSaveDetails();
            } else {
              if (type === SEND_OTP_TYPES.changePhone) {
                changePhoneAPI();
                return;
              }
              lpc.setVisible(false);
              if (isWebPlatform && onVerificationSuccess) {
                setAuthCredentials({
                  //setting the credentials using the already existing authCredentials and params
                  ...authCredentials,
                  ...params,
                });
                onVerificationSuccess(); //this will move to next step (password recovery) in this case, plz check phoneverification.js
              } else {
                navigation.navigate(nextScreen, params);
              }
            }
          } else {
            lpc.setVisible(false);
            setHasError(true);
            updateNumAttempts();
          }
        })
        .catch(error => {
          // DLog(error);
          setHasError(true);
          updateNumAttempts();
          lpc.setVisible(false);
          esc.showError(error);
        });
    };

    const changePopupVisibility = () => {
      setHasError(false);
      setValue('');
      setCodeResendVisibility(false);
    };

    return (
      <View
        testID={tID('VerifyCode-View-Container')}
        style={isWebPlatform ? CommonStyles.full_flex : styles.root}>
        {codeResendVisibility && (
          <CodeResend
            codeResendVisibility={codeResendVisibility}
            resendCode={resendCode}
            changePopupVisibility={changePopupVisibility}
            phoneNo={phoneNo}
          />
        )}
        {showContactSupport && (
          <ContactSupport
            showContactSupport={showContactSupport}
            setShowContactSupport={setShowContactSupport}
          />
        )}
        <Text
          testID={tID('VerifyCode-Text-Code')}
          style={[styles.inputTitleStyle, isWebPlatform && CommonStyles.mt_32]}>
          Code
        </Text>

        <View style={styles.fullFlex}>
          <CodeField
            testID={tID('VerifyCode-Input-Code')}
            ref={blurFullfillRef}
            {...props}
            caretHidden={false}
            value={value}
            onChangeText={setValue}
            cellCount={CELL_COUNT}
            rootStyle={[
              styles.codeFieldRoot,
              isWebPlatform && CommonStyles.justify_start,
            ]}
            keyboardType="number-pad"
            textContentType="oneTimeCode"
            autoComplete="sms-otp"
            autoFocus={true}
            inputAccessoryViewID={'verifyCodeDone'}
            renderCell={({index, symbol, isFocused}) => (
              <Text
                testID={tID(`VerifyCode-Text-Cursor${index + 1}`)}
                key={index}
                style={[
                  styles.cell(hasError),
                  isWebPlatform ? styles.webCell : styles.mobileCell,
                  isFocused && styles.focusCell,
                ]}
                onLayout={getCellOnLayoutHandler(index)}>
                {symbol || (isFocused ? <Cursor /> : null)}
              </Text>
            )}
          />
          {hasError && (
            <DIAText
              testID={tID('VerifyCode-DIAText-Incorrect')}
              variation="Text1Regular"
              topMargin={8}
              color={colors.danger}>
              Incorrect code! Try again.
            </DIAText>
          )}
        </View>
        {showButtons && (
          <View style={styles.buttonsContainer}>
            <Button
              testID={tID('VerifyCode-Button-Resend')}
              style={styles.leftButton}
              onPress={() => resendCode(phoneNo)}
              title="Resend Code"
            />
            <Button
              testID={tID('VerifyCode-Button-Verify')}
              disabled={value.length != 6}
              style={styles.rightButton}
              onPress={verifyOTP}
              title="Verify Code"
              buttonType={BUTTON_TYPES.Filled}
              titleColor={colors.textOnAccent}
            />
          </View>
        )}
        {Platform.OS == 'ios' && (
          <InputAccessoryView nativeID={'verifyCodeDone'}>
            <View style={styles.accessoryContainer}>
              <View />
              <Text
                testID={tID('VerifyCode-Text-Done')}
                style={styles.doneButton}
                onPress={Keyboard.dismiss}>
                Done
              </Text>
            </View>
          </InputAccessoryView>
        )}
      </View>
    );
  },
);

export default VerifyCode;
//This common component is being used by
// 1. Signup phone verification
// 2. change phone number verification
// 3. forgor password phone verification
